/**
 * Renders tool results as compact cards within the chat.
 * Each tool type gets a tailored display.
 */
export default function ToolResultCard({ toolName, result }) {
  if (result?.error) {
    const message = /(?:class|student|learner).*(?:not found|matching)/i.test(result.error)
      ? result.error
      : 'Something went wrong. Please try again.';
    return (
      <div className="alert alert-error alert-sm text-sm my-2">
        {message}
      </div>
    );
  }

  switch (toolName) {
    case 'query_students':
      return <StudentListCard result={result} />;
    case 'get_student_discipline':
      return <DisciplineCard result={result} />;
    case 'get_class_overview':
      return <ClassOverviewCard result={result} />;
    case 'issue_mention':
      return <MentionResultCard result={result} />;
    default:
      return (
        <div className="text-xs text-base-content/50 my-1">
          Tool: {toolName}
        </div>
      );
  }
}

function StudentListCard({ result }) {
  if (!result?.students?.length) {
    return null;
  }

  return (
    <div className="card card-compact bg-base-200 my-2">
      <div className="card-body p-3">
        <h4 className="text-xs font-semibold text-base-content/70 uppercase tracking-wide">
          {result.className ? `${result.className} — ` : ''}{result.count} learner{result.count !== 1 ? 's' : ''}
        </h4>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {result.students.map((s) => (
            <span key={s.id} className="badge badge-sm badge-ghost">
              {s.name}
              {s.classes?.length > 0 && (
                <span className="text-base-content/40 ml-1">{s.classes[0]}</span>
              )}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function DisciplineCard({ result }) {
  return (
    <div className="card card-compact bg-base-200 my-2">
      <div className="card-body p-3">
        <h4 className="text-xs font-semibold text-base-content/70 uppercase tracking-wide">
          {result.studentName} — {result.period}
        </h4>
        <div className="flex gap-4 mt-1">
          <div className="text-sm">
            <span className="text-success font-medium">{result.summary?.totalMerits || 0}</span>
            <span className="text-base-content/50 ml-1">merits</span>
          </div>
          <div className="text-sm">
            <span className="text-error font-medium">{result.summary?.totalDemerits || 0}</span>
            <span className="text-base-content/50 ml-1">demerits</span>
          </div>
          <div className="text-sm">
            <span className="font-medium">{result.summary?.netScore || 0}</span>
            <span className="text-base-content/50 ml-1">net</span>
          </div>
        </div>
        {result.recentMentions?.length > 0 && (
          <div className="mt-2 space-y-1">
            {result.recentMentions.slice(0, 5).map((m, i) => (
              <div key={i} className="text-xs flex items-center gap-2">
                <span className={m.type === 'merit' ? 'text-success' : 'text-error'}>
                  {m.type === 'merit' ? '+' : '−'}
                </span>
                <span className="text-base-content/70">{m.reason}</span>
                <span className="text-base-content/30 ml-auto">{m.date}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ClassOverviewCard({ result }) {
  return (
    <div className="card card-compact bg-base-200 my-2">
      <div className="card-body p-3">
        <h4 className="text-xs font-semibold text-base-content/70 uppercase tracking-wide">
          {result.className} — {result.studentCount} learners
        </h4>
        {result.recentActivity && (
          <div className="flex gap-4 mt-1 text-sm">
            <span className="text-success">{result.recentActivity.totalMerits} merits</span>
            <span className="text-error">{result.recentActivity.totalDemerits} demerits</span>
            <span className="text-base-content/50">(7 days)</span>
          </div>
        )}
      </div>
    </div>
  );
}

function MentionResultCard({ result }) {
  if (!result?.success) {
    return (
      <div className="text-xs text-error my-1">
        Failed to issue mention
      </div>
    );
  }

  return (
    <div className="text-xs text-success my-1">
      {result.summary}
    </div>
  );
}
